import { inject, injectable } from "inversify";
import type * as THREE from "three";
import { Logger } from "@designboticteam/core";
import { CursorSnappingService } from "../../../../input/CursorSnappingService";
import { EDGE_PICK_PX, VERTEX_PICK_PX } from "../../../../input/pickTolerance";
import { Workspace3DStoreService } from "../../../../services/Workspace3DStoreService";
import { SceneService } from "../../../../stage/SceneService";
import { projectPointOntoSegment2D } from "../../../../utils/projectPointOntoSegment2D";
import type { Shape } from "../../../entities/_base/Shape";
import { Polyline } from "../../../entities/Polyline";
import type { IShapeEditHandler } from "../IShapeEditHandler";
import { EdgeOffsetGuides } from "./EdgeOffsetGuides";

@injectable()
class EdgeEditHandler implements IShapeEditHandler {
	private readonly logger = new Logger("📐 EdgeEditHandler", { color: "#ffffff", backgroundColor: "#b45309" });

	private readonly guides = new EdgeOffsetGuides();

	// The geometry the current selection is measured from. Recaptured whenever the selection changes,
	// so the offset field is absolute: typing 5 after 3 gives 5 from the original, not 8.
	private basePoints: Array<THREE.Vector3> = [];

	private targetPolyline: Polyline | null = null;
	private selectedEdgeIndex: number = -1;

	// What the cursor was over when the button went down. The drag only starts once the pointer has
	// travelled past DRAG_THRESHOLD_PX, by which point it may have left the edge it pressed on — so
	// re-picking at drag start would grab less than the hover feedback promised.
	private edgeAtPointerDown: number = -1;

	// The offset is always measured from the geometry as it was when the drag began: recomputing it
	// from the live points would move the helper lines mid-drag and let the edge run away.
	private dragStartPoints: Array<THREE.Vector3> = [];
	private dragStartPosition: THREE.Vector3 | null = null;
	private edgeNormal: THREE.Vector3 | null = null;

	constructor(
		@inject(CursorSnappingService) private readonly cursorSnappingService: CursorSnappingService,
		@inject(SceneService) private readonly sceneService: SceneService,
		@inject(Workspace3DStoreService) private readonly workspace3DStoreService: Workspace3DStoreService,
	) {
		this.logger.log("constructed");
	}

	public setTarget(target: Shape | null): void {
		this.targetPolyline?.setHighlightedEdge({ edgeIndex: null });
		this.targetPolyline?.setSelectedEdges({ edgeIndices: [] });
		this.targetPolyline = target instanceof Polyline ? target : null;
		this.resetDragState();
		this.applySelection({ edgeIndices: [] });
	}

	/**
	 * Toggles the edge under the cursor in or out of the selection. Returns whether it consumed the
	 * tap — ShapeEditingService must not also re-run shape selection, which would clear this state.
	 */
	public onTap(): boolean {
		if (this.targetPolyline === null) {
			return false;
		}

		const edgeIndex = this.findEdgeUnderCursor();

		if (edgeIndex === -1) {
			return false;
		}

		const current = this.workspace3DStoreService.store.getState().tools.selectedEdgeIndices;
		const next = current.includes(edgeIndex) ? current.filter((entry) => entry !== edgeIndex) : [...current, edgeIndex];

		this.applySelection({ edgeIndices: next });
		return true;
	}

	/**
	 * Moves the selected edges to `distance` from the captured base geometry. Always recomputed from
	 * the base, so sliding the value back and forth never degrades the shape and 0 restores it
	 * exactly — the same contract as Zone.applyOffset.
	 */
	public setOffset({ distance }: { distance: number }): boolean {
		const { selectedEdgeIndices } = this.workspace3DStoreService.store.getState().tools;

		if (this.targetPolyline === null || selectedEdgeIndices.length === 0 || Number.isFinite(distance) === false) {
			return false;
		}

		// Restore the base, then offset from it — the same two-step the drag path uses, and it keeps
		// the loop/chain distinction inside the entity. The selection overlay follows because
		// syncShapeView redraws it from the indices the entity holds.
		this.targetPolyline.setPoints({ points: this.basePoints });

		// A whole ring goes through Polygon.offset, which is clipper-backed and survives the topology
		// changes a deep inset causes on a concave shape. The per-edge construction cannot: it would
		// throw vertices into spikes. Polyline.offset is per-edge anyway, so open chains are unaffected.
		const isWholeShape = selectedEdgeIndices.length === this.targetPolyline.getEdgeCount();
		return isWholeShape ? this.targetPolyline.offset({ distance }) : this.targetPolyline.offsetEdges({ edgeIndices: selectedEdgeIndices, distance });
	}

	public clearSelection(): void {
		this.applySelection({ edgeIndices: [] });
	}

	public selectAllEdges(): void {
		const edgeCount = this.targetPolyline?.getEdgeCount() ?? 0;
		this.applySelection({ edgeIndices: Array.from({ length: edgeCount }, (_value, index) => index) });
	}

	private applySelection({ edgeIndices }: { edgeIndices: ReadonlyArray<number> }): void {
		// A new selection measures from wherever the shape is now.
		this.basePoints = this.targetPolyline?.getPoints() ?? [];
		this.workspace3DStoreService.store.setState((state) => ({ tools: { ...state.tools, selectedEdgeIndices: [...edgeIndices] } }));
		this.targetPolyline?.setSelectedEdges({ edgeIndices });
	}

	public isEngaged(): boolean {
		return this.selectedEdgeIndex !== -1;
	}

	public onPointerMove(): void {
		if (this.targetPolyline === null) {
			return;
		}

		// Mid-drag the grabbed edge stays marked, wherever the cursor has wandered to.
		if (this.isEngaged()) {
			return;
		}

		const edgeIndex = this.findEdgeUnderCursor();
		this.targetPolyline.setHighlightedEdge({ edgeIndex: edgeIndex === -1 ? null : edgeIndex });
	}

	public onPointerDown(): void {
		this.edgeAtPointerDown = this.findEdgeUnderCursor();
	}

	public onDragStart(): void {
		// Deliberately not a fresh pick: the target is the one the press landed on. Read before the
		// reset below, which clears the capture along with the rest of the drag state.
		const edgeIndex = this.edgeAtPointerDown;

		// Only the drag state is cleared, never the highlight: a grab that fails a guard below must
		// leave the hover feedback standing rather than blinking it off.
		this.resetDragState();

		if (this.targetPolyline === null) {
			return;
		}

		const { worldPosition } = this.cursorSnappingService.pickPosition();

		if (worldPosition === null || edgeIndex === -1 || edgeIndex >= this.targetPolyline.getEdgeCount()) {
			return;
		}

		// A selected edge is driven by the offset field only — dragging it would fight the base
		// geometry that field measures from.
		if (this.workspace3DStoreService.store.getState().tools.selectedEdgeIndices.includes(edgeIndex)) {
			return;
		}

		const points = this.targetPolyline.getPoints();
		const edgeNormal = this.targetPolyline.getEdgeNormal({ edgeIndex });
		const helperLines = this.targetPolyline.getEdgeHelperLines({ edgeIndex });

		if (edgeNormal === null || helperLines === null) {
			return;
		}

		this.selectedEdgeIndex = edgeIndex;
		this.dragStartPoints = points;
		this.dragStartPosition = worldPosition.clone();
		this.edgeNormal = edgeNormal;

		// The whole shape, not just the edge: excluded vertices are compacted out of the snap list, so
		// dropping only two of them would stitch their neighbours into an edge that does not exist.
		this.cursorSnappingService.setSnapExclusion([{ sceneObject: this.targetPolyline.line, vertexIndices: points.map((_point, index) => index) }]);

		this.targetPolyline.setHighlightedEdge({ edgeIndex });

		this.sceneService.scene.add(this.guides.sceneObject);
		this.guides.show({
			helperLines,
			edgeStart: points[edgeIndex],
			edgeEnd: points[(edgeIndex + 1) % points.length],
		});
	}

	public onDragMove(): void {
		if (this.isEngaged() === false || this.targetPolyline === null) {
			return;
		}

		const { worldPosition } = this.cursorSnappingService.pickPosition();

		if (worldPosition === null || this.dragStartPosition === null || this.edgeNormal === null) {
			return;
		}

		const distance = worldPosition.clone().sub(this.dragStartPosition).dot(this.edgeNormal);

		this.targetPolyline.setPoints({ points: this.dragStartPoints });
		this.targetPolyline.offsetEdge({ edgeIndex: this.selectedEdgeIndex, distance });
	}

	public onDragEnd(): void {
		if (this.isEngaged() && this.targetPolyline !== null) {
			this.targetPolyline.removeSelfIntersections();
		}

		this.resetDragState();
		this.targetPolyline?.setHighlightedEdge({ edgeIndex: null });

		const { selectedEdgeIndices } = this.workspace3DStoreService.store.getState().tools;
		const edgeCount = this.targetPolyline?.getEdgeCount() ?? 0;

		// The rewrite may have merged vertices (setPoints drops coincident ones); an index past the new
		// edge count would point at nothing.
		if (selectedEdgeIndices.some((edgeIndex) => edgeIndex >= edgeCount)) {
			this.applySelection({ edgeIndices: selectedEdgeIndices.filter((edgeIndex) => edgeIndex < edgeCount) });
			return;
		}

		// Any drag — a vertex moved by the other handler included, since ShapeEditingService ends
		// both — rewrote the geometry the selection was measured against. Re-anchor, or the next value
		// typed into the offset field would restore the pre-drag shape.
		if (selectedEdgeIndices.length > 0) {
			this.basePoints = this.targetPolyline?.getPoints() ?? [];
		}
	}

	/**
	 * Index of the edge the cursor is over, or -1. Measured in screen pixels so the grab area does
	 * not shrink with the camera. A cursor near a vertex belongs to VertexEditHandler, so no edge is
	 * reported there.
	 */
	private findEdgeUnderCursor(): number {
		if (this.targetPolyline === null) {
			return -1;
		}

		const { screenPosition } = this.cursorSnappingService.pickPosition();

		if (screenPosition === null) {
			return -1;
		}

		// Points behind the camera project to null; an edge with either end missing is skipped below.
		const pointsOnScreen = this.targetPolyline.getPoints().map((point) => this.cursorSnappingService.getPositionOnViewport(point));

		const isNearVertex = pointsOnScreen.some((vertex) => {
			if (vertex === null) {
				return false;
			}

			return Math.hypot(screenPosition.x - vertex.x, screenPosition.y - vertex.y) < VERTEX_PICK_PX;
		});

		if (isNearVertex) {
			return -1;
		}

		let lowestDistance = EDGE_PICK_PX;
		let nearestIndex = -1;

		for (let edgeIndex = 0; edgeIndex < this.targetPolyline.getEdgeCount(); edgeIndex++) {
			const start = pointsOnScreen[edgeIndex];
			const end = pointsOnScreen[(edgeIndex + 1) % pointsOnScreen.length];

			if (start === null || end === null) {
				continue;
			}

			const closestPoint = projectPointOntoSegment2D({ point: screenPosition, start, end });
			const distance = Math.hypot(screenPosition.x - closestPoint.x, screenPosition.y - closestPoint.y);

			if (distance < lowestDistance) {
				lowestDistance = distance;
				nearestIndex = edgeIndex;
			}
		}

		return nearestIndex;
	}

	private resetDragState(): void {
		this.guides.hide();
		this.guides.sceneObject.removeFromParent();
		this.cursorSnappingService.setSnapExclusion([]);

		this.selectedEdgeIndex = -1;
		this.edgeAtPointerDown = -1;
		this.dragStartPoints = [];
		this.dragStartPosition = null;
		this.edgeNormal = null;
	}
}

export { EdgeEditHandler };
